import React, { useCallback, useEffect } from "react";
import ApiClient from "../../services/ApiClient";
import _ from "lodash";
import useForm from "../../hooks/useForm";
import SubmitButton from "../Button/SubmitButton";
import { Grid, MenuItem } from "@mui/material";
import { requiredFieldDefault, textFieldDefault } from "../../services/validationRules";
import ValidatorTextField from "../Validator/ValidatorTextField";
import ValidatorSelect from "../Validator/ValidatorSelect";
import { formValue } from "../../services/formServiceFunctions";
import { getFlatTranslationPathsWithLabel, translate } from "../../services/Translations/translatorService";
import Template from "../../types/Template";
import { UseForm } from "../../types/UseForm";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import { useCurrentEditor } from "@tiptap/react";
import { getEditorHtml } from "../../services/Editor/editorFunctions";
import AddPlaceholder from "../LbEditor/Toolbar/AddPlaceholder";
import { litigationProductClassNames } from "../../types/AbstractCase";

type TemplateEditorProps = {
  template?: Template;
  onSubmitCallback?: () => Promise<void>;
  initialProduct?: string;
  type: string;
  withSubject?: boolean;
};

const TemplateEditor = (props: TemplateEditorProps) => {
  return (
    <LegalbirdTipTapEditorProvider placeholder={"Entwurf"}>
      <TemplateEditorInner {...props} />
    </LegalbirdTipTapEditorProvider>
  );
};

const TemplateEditorInner = ({
  template,
  onSubmitCallback,
  initialProduct,
  type,
  withSubject,
}: TemplateEditorProps) => {
  const { editor } = useCurrentEditor();

  useEffect(() => {
    if (!editor || !template) {
      return;
    }
    editor.commands.setContent(template.content);
  }, [editor, template]);

  const onSubmit = async ({ values }: UseForm["values"]) => {
    let valuesToPersist = _.merge(
      {
        type: type,
      },
      values
    );

    const htmlContent = getEditorHtml(editor);

    valuesToPersist.content = htmlContent ? _.unescape(htmlContent).trim() : null;

    if (!template) {
      await ApiClient.post("templates", { body: JSON.stringify(valuesToPersist) });
      clearForm();
    } else {
      await ApiClient.patch("templates/" + template.id, { body: JSON.stringify(valuesToPersist) });
    }
    if (_.isFunction(onSubmitCallback)) {
      await onSubmitCallback();
    }
  };

  let initialValues;

  if (template) {
    initialValues = {
      productClassName: template.productClassName,
      label: template.label,
      subject: template.subject,
      recipientTypes: template.recipientTypes,
      context: template.context,
    };
  } else {
    initialValues = {
      productClassName: initialProduct,
      label: "",
      subject: "",
      recipientTypes: [],
      context: [],
    };
  }

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    registerValidators,
    touchedValues,
    isLoading,
    clearForm,
  }: UseForm = useForm({
    initialValues,
    onSubmit,
  });

  const getPlaceholders = useCallback(() => {
    const translationPathsWithLabel = getFlatTranslationPathsWithLabel(values.productClassName);
    return _.pickBy(translationPathsWithLabel, (value, key) => _.endsWith(key, "label"));
  }, [values.productClassName]);

  if (!editor) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: "center",
          alignItems: "center"
        }}>
        <Grid size={6}>
          <ValidatorSelect
            disabled={!!template}
            label={"Rechtsprodukt"}
            name={"productClassName"}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values["productClassName"]}
          >
            <MenuItem value={"divorce"}>Scheidung</MenuItem>
            <MenuItem value={"settlement"}>Abfindung</MenuItem>
            <MenuItem value={"traffic"}>Verkehr</MenuItem>
            <MenuItem value={"trafficAccident"}>Verkehrsunfall</MenuItem>
            <MenuItem value={"death"}>Tod und Erbe</MenuItem>
            <MenuItem value={"alimony"}>Unterhalt</MenuItem>
            <MenuItem value={"patientDecree"}>Patientenverfügung</MenuItem>
            <MenuItem value={"powerOfAttorney"}>Vorsorgevollmacht</MenuItem>
            <MenuItem value={"familyLawCase"}>Familienrecht</MenuItem>
            <MenuItem value={"contractLaw"}>Vertragsrecht</MenuItem>
            <MenuItem value={"dataLeakContract"}>Datenweitergabe</MenuItem>
            <MenuItem value={"rentalContract"}>{translate("rentalContract.label")}</MenuItem>
            <MenuItem value={"generalCivilLaw"}>{translate("generalCivilLaw.label")}</MenuItem>
            <MenuItem value={"employmentContract"}>{translate("employmentContract.label")}</MenuItem>
            <MenuItem value={"civilServiceLaw"}>{translate("civilServiceLaw.label")}</MenuItem>
            <MenuItem value={"general"}>Produktübergreifend</MenuItem>
          </ValidatorSelect>
        </Grid>
        {type === "email" && (
          <Grid size={6}>
            <ValidatorSelect
              label={"Empfänger"}
              multiple
              validators={requiredFieldDefault}
              registerValidators={registerValidators}
              name={"recipientTypes"}
              value={formValue(values, "recipientTypes")}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors["recipientTypes"]}
              helperText={errors["recipientTypes"]}
            >
              <MenuItem value={"customer"}>Mandantschaft</MenuItem>
              <MenuItem
                disabled={
                  !["general", "rentalContract", "contractLaw", "dataLeakContract", "employmentContract"].includes(
                    values["productClassName"]
                  )
                }
                value={"contractualPartner"}
              >
                Gegenseite
              </MenuItem>
              <MenuItem
                disabled={
                  !["general", "rentalContract", "contractLaw", "dataLeakContract", "employmentContract"].includes(
                    values["productClassName"]
                  )
                }
                value={"debtCollectionAgency"}
              >
                Inkassobüro
              </MenuItem>
              <MenuItem
                disabled={!["general", "rentalContract"].includes(values["productClassName"])}
                value={"propertyManager"}
              >
                Hausverwaltung
              </MenuItem>
              <MenuItem
                disabled={
                  ![
                    "general",
                    "rentalContract",
                    "contractLaw",
                    "dataLeakContract",
                    "settlement",
                    "divorce",
                    "employmentContract",
                  ].includes(values["productClassName"])
                }
                value={"opponentLawFirm"}
              >
                Anwalt Gegenseite
              </MenuItem>
              <MenuItem
                disabled={!["general", "settlement"].includes(values["productClassName"])}
                value={"employerOrganization"}
              >
                E-Mail-Adresse Gegenseite
              </MenuItem>
              <MenuItem value={"processParticipants"}>Weitere Prozessbeteiligte</MenuItem>
              <MenuItem value={"insurance"}>Rechtsschutzversicherung</MenuItem>
              <MenuItem value={"advoAssistRepresentative"}>Terminsvertreter</MenuItem>
              <MenuItem
                value={"opposingLiabilityInsurance"}
                disabled={![...litigationProductClassNames, "contractLaw"].includes(values["productClassName"])}
              >
                Haftpflichtversicherung der Gegenseite
              </MenuItem>
            </ValidatorSelect>
          </Grid>
        )}
        <Grid size={type === "email" ? 12 : 6}>
          <ValidatorTextField
            label={"Vorlage"}
            name={"label"}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values["label"]}
            registerValidators={registerValidators}
            validators={[...textFieldDefault, ...requiredFieldDefault]}
            error={!!errors["label"]}
            helperText={errors["label"]}
            isHighlighted={undefined}
            initialDependentValidationFields={[]}
          />
        </Grid>
        {type === "text" && (
          <Grid size={12}>
            <ValidatorSelect
              label={"Use Case"}
              multiple
              validators={requiredFieldDefault}
              registerValidators={registerValidators}
              name={"context"}
              value={values["context"]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors["context"]}
            >
              <MenuItem value={"message"}>Nachrichten</MenuItem>
              <MenuItem value={"legalDocuments"}>Schriftsätze</MenuItem>
              <MenuItem value={"inquiry"}>Anfragen</MenuItem>
            </ValidatorSelect>
          </Grid>
        )}
        {withSubject && (
          <Grid size={12}>
            <ValidatorTextField
              label={"Betreff"}
              name={"subject"}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values["subject"]}
              registerValidators={registerValidators}
              validators={[...textFieldDefault, ...requiredFieldDefault]}
              error={!!errors["subject"]}
              helperText={errors["subject"]}
              multiline
              isHighlighted={undefined}
              initialDependentValidationFields={[]}
            />
          </Grid>
        )}
        <Grid size={12}>
          <LegalbirdTipTapEditor
            additionalToolbarActions={[
              <AddPlaceholder placeholders={getPlaceholders()} productClassName={values.productClassName} />,
            ]}
          />
        </Grid>
        <Grid size={12}>
          <SubmitButton
            error={_.keys(errors).length > 0}
            isLoading={isLoading}
            disabled={_.keys(touchedValues).length === 0 && editor.getHTML() === template?.content}
            variant={"contained"}
            type={"submit"}
          >
            Vorlage speichern
          </SubmitButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default TemplateEditor;
